import styled from "@emotion/styled";
import { FC } from "react";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { Avatar, DesignToken, Icon, Tag } from "@livechat/design-system-react-components";

import { selectAgents, selectAgentsAlphabetically } from "store/entities/agents";
import { withVar } from "themes/helpers";

import { validAgent } from "../validators";

interface IProps {
  agentID: string | null | undefined;
  randomAgent: boolean | undefined;
  roundRobin: boolean | undefined;
  agentType: "agent" | "follower";
}

export const AccordionAgentTag: FC<IProps> = ({ agentID, randomAgent, roundRobin, agentType }) => {
  const allAgents = useSelector(selectAgentsAlphabetically);
  const agentsById = useSelector(selectAgents);

  const invalidAgent = validAgent(agentID, randomAgent, roundRobin, allAgents);

  const getAgentText = (): string => {
    if (roundRobin) {
      return "Round-robin";
    }

    if (randomAgent) {
      return "Random agent/admin";
    }

    if (agentID === null) {
      return "Unassigned";
    }

    if (agentID === undefined) {
      return "Select agent…";
    }

    if (agentsById[agentID]) {
      return agentsById[agentID].name;
    }

    return agentType === "agent" ? "Agent removed" : "Follower removed";
  };

  return (
    <AgentTag
      kind={invalidAgent ? "error" : "default"}
      leftNode={
        invalidAgent ? (
          <Icon source={Error} customColor={withVar(DesignToken.ActionNegativeDefault)} />
        ) : (
          <AgentTagAvatar agentID={agentID} randomAgent={randomAgent} roundRobin={roundRobin} agentType={agentType} />
        )
      }
    >
      {getAgentText()}
    </AgentTag>
  );
};

const AgentTagAvatar: FC<IProps> = ({ agentID, randomAgent, roundRobin }) => {
  const agentsById = useSelector(selectAgents);

  if (!agentID || randomAgent || roundRobin) {
    return null;
  }

  const agent = agentsById[agentID];

  return (
    <Avatar size="xxxsmall" type={agent.avatar ? "image" : "text"} text={agent.name} src={agent.avatar ?? undefined} />
  );
};

const AgentTag = styled(Tag)`
  max-width: 200px;
`;
